import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { BillingRhythm } from '../../billingPositions/entities/BillingPosition.js';
import { Person } from '../../users/entities/person.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { viewerCanOnPerson } from '../../permissions/middlewares/abacFieldMiddleware.js';
import { Booking } from '../../bookings/entities/Booking.js';
import {
  clampToBookingRuntime,
  hasFixedAmount,
  isBackdatedStart,
  isStartMovableByOwner,
} from '../api/mandateActivity.js';
import { getMandatePhase, isAcceptance, MandatePhase } from '../api/MandatStatus.js';
import { SepaMandateActions } from '../SepaMandatePolicy.js';
import { assertMandateNotInBilling, getLockingGroupForMandate } from '../server/mandateBillingLock.js';

@Entity()
export class SepaMandat extends MongoEntity {
  static override _class = 'SepaMandat';
  static override _collectionName = 'sepa-mandates';

  constructor(doc: Partial<SepaMandat>) {
    super(doc);
  }

  // references the booking
  @Field(() => ID)
  bookingID!: string;

  /**
   * There is a job which remounts mandates to new bookings. For example the Spende is bound to a schoolyear but we remount it to the most recent schoolyear every year
   */
  @Field(() => [ID], { default: v => v ?? [] })
  pastBookingIDs: Maybe<string[]>;

  /**
   * References the groupID from which this mandate was generated. Should be the first groupID of the booking this mandates belongs to.
   */
  @Field(() => ID)
  groupID: Maybe<string>;

  /**
   * Captures the schoolID this mandate belonge to. The school should also be accessible trough the groupID parents, as is should be connected to a school somewhere down the line. This is just redudancy for quick lookups with using recursive resolvers everytime
   */
  @Field(() => ID)
  schoolID: Maybe<string>;

  // =========
  // Base Information
  // =========

  /**
   * Is the same as the name of the billingPosition this mandate is for
   */
  @Field(() => String)
  name: Maybe<string>;

  @Field(() => String)
  kuerzel: Maybe<string>;

  @Field(() => Int)
  amountInCents: Maybe<number>;

  /**
   * The mandates referenze for the mandate. This is a reversible encoded counter.
   */
  @Field(() => String)
  mandateReference: Maybe<string>;

  // A custom IBAN for the sepa mandate
  @Field(() => String)
  iban: Maybe<string>;

  // =========
  // Time based
  // =========

  /**
   * Whether the mandate runs *today* - its phase is RUNNING (`getMandatePhase`), so the user list
   * agrees with the card. Day precision, so it turns over with the calendar and not with a write -
   * which is why it is stored nowhere: neither on the mandate, nor in the person view, where
   * `materializedUserSelectionSet` explicitly deletes it. `getMandatePhaseSelector` is the Mongo
   * counterpart the filter uses.
   *
   * The answer lives in the stored fields, and those are not guaranteed to be loaded with it: the
   * user list projects the person view down to the configured columns, so a column showing only
   * `active` left the resolver with a bare `{ _id, _class }`. Reload the mandate in that case;
   * `loadMongoData` batches all reloads of one request into a single query.
   */
  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(() => Boolean, {
    resolve: function (this: SepaMandat) {
      return async () => {
        // an unset field is `null`, only an unloaded one is `undefined`
        const unloaded = [this.accepted, this.start, this.end, this.rhythm, this.billedOn].includes(undefined);
        const subject = unloaded && this._id ? await loadMongoData(SepaMandat, this._id) : this;

        return !!subject && getMandatePhase(subject) === MandatePhase.RUNNING;
      };
    },
  })
  active: Maybe<boolean>;

  /**
   * Whether the mandate's billing group is in billing right now (`sepa/server/mandateBillingLock.ts`),
   * whatever its rhythm and whether accepted or not: the card then holds it read only and refuses
   * consent for everybody but the administrative SEPA roles. Day dependent like `active`, so stored
   * nowhere and deleted from the person view the same way.
   */
  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(() => Boolean, {
    resolve: function (this: SepaMandat) {
      return async () => {
        const subject = this.bookingID === undefined && this._id ? await loadMongoData(SepaMandat, this._id) : this;

        return !!subject && !!(await getLockingGroupForMandate(subject));
      };
    },
  })
  billingLocked: Maybe<boolean>;

  /**
   * The first day the mandate runs: the day it was accepted (the card and the bulk accept write
   * today), never the booking's start - the export judges the booking's runtime on the booking
   * itself. Unset on a template and on an unaccepted draft.
   */
  @Field(() => Date)
  start: Maybe<Date>;

  /**
   * The last day the mandate runs. For a one-time mandate the export that collected it writes its
   * Stichtag, and "Mandat beenden" writes yesterday (a withdrawal); whether it was collected is
   * `billedOn`, not this date.
   */
  @Field(() => Date)
  end: Maybe<Date>;

  @Field(() => String)
  rhythm: Maybe<BillingRhythm>;

  /**
   * The Stichtag of the last lock export that collected this mandate. Decides, together with the
   * booking's own marker, when the mandate is due again (`isDueOn`): a yearly one twelve months
   * later, a one-time one never. Kept on the mandate rather than the booking because two guardians
   * may pledge on the same booking, each in their own rhythm.
   */
  @Field(() => Date)
  billedOn: Maybe<Date>;

  // =========
  // Acceptance
  // =========

  // if a person accepted the booking
  @Field(() => Boolean, { default: v => v ?? false })
  accepted: Maybe<boolean>;

  @Field(() => Date)
  acceptedAt: Maybe<Date>;

  @Field(() => ID)
  acceptedByID: Maybe<string>;

  @Dependencies<typeof SepaMandat>({
    ids: function (this) {
      return [this.acceptedByID].filter(isNotNullish);
    },
    classes: () => [Person],
  })
  @Field(type => String, {
    resolve: function (this: SepaMandat) {
      return async () => {
        if (this.acceptedPersonName) return this.acceptedPersonName;
        // use a raw resolver here as SBS cannot see each other but need to see the name of the person which accepted the mandat
        const userCol = await getRawCollection<EntityData<Person>>(Person._collectionName);
        if (!this.acceptedByID) return '---';
        const user = await userCol.findOne({ _id: this.acceptedByID });
        if (!user?.profilDaten?.stammdaten?.vorname || !user.profilDaten.stammdaten.nachname)
          return user?.email ? `${user.email} (${user.debitorennummer})` : (user?.debitorennummer ?? '---');
        return `${user.profilDaten.stammdaten.vorname} ${user.profilDaten.stammdaten.nachname} (${user.debitorennummer})`;
      };
    },
  })
  acceptedPersonName: Maybe<string>;

  // =========
  // Custom Bank Information
  // =========

  @Field(() => Boolean, { default: v => v ?? false })
  canEditAmount: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  canEditRhythm: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  isDynamic: Maybe<boolean>;

  /**
   * This flag is set if the bookings of the mandate was phased out
   */
  @Field(() => Boolean)
  oldBooking: Maybe<boolean>;

  /**
   * Every write that goes through the entity - the card, the bulk accept, both resets and the
   * client's direct `updateEntity` - passes {@link assertOwnerMayWrite}. The export's own writes are
   * raw collection writes and bypass this on purpose.
   */
  static override async beforeCreate(entity: SepaMandat): Promise<void> {
    await bindRuntimeToBooking(null, entity);
    await assertOwnerMayWrite(null, entity);

    return MongoEntity.beforeCreate(entity);
  }

  /** Runs before the modifier is built - in `beforeUpdate` a changed runtime would no longer be written. */
  static override async transformBeforeUpdate(
    oldEntity: PartialEntityData<SepaMandat>,
    update: SepaMandat,
  ): Promise<void> {
    await bindRuntimeToBooking(oldEntity, update);

    return MongoEntity.transformBeforeUpdate(oldEntity, update);
  }

  static override async beforeUpdate(oldEntity: PartialEntityData<SepaMandat>, update: SepaMandat): Promise<void> {
    assertRhythmUnchanged(oldEntity, update);
    await assertOwnerMayWrite(oldEntity, update);

    return MongoEntity.beforeUpdate(oldEntity, update);
  }
}

/**
 * Whoever writes a mandate with a dictated amount - the card, the bulk accept, a reset - it runs within
 * its booking ({@link clampToBookingRuntime}): accepted today for a booking that starts in August, it
 * starts in August; an end past the booking's is cut back to it. The cut runtime is what gets written,
 * and what {@link assertOwnerMayWrite} judges. `stored` is null on a create.
 */
async function bindRuntimeToBooking(stored: Maybe<PartialEntityData<SepaMandat>>, update: SepaMandat): Promise<void> {
  // the mandate as it is after the write - `updateEntity` may send only some fields
  const next = {
    bookingID: update.bookingID ?? stored?.bookingID,
    canEditAmount: update.has('canEditAmount') ? update.canEditAmount : stored?.canEditAmount,
    start: update.has('start') ? update.start : stored?.start,
    end: update.has('end') ? update.end : stored?.end,
  };
  if (!next.bookingID || !hasFixedAmount(next)) return;

  const bookingCol = await getRawCollection<EntityData<Booking>>(Booking._collectionName);
  const booking = await bookingCol.findOne({ _id: next.bookingID }, { projection: { startDate: 1, endDate: 1 } });
  if (!booking) return;

  const runtime = clampToBookingRuntime(next, booking);
  if (runtime.start?.getTime() !== next.start?.getTime()) update.start = runtime.start;
  if (runtime.end?.getTime() !== next.end?.getTime()) update.end = runtime.end;
}

const PLEDGE_RHYTHMS: Maybe<BillingRhythm>[] = [BillingRhythm.MONTHLY, BillingRhythm.YEARLY];

/**
 * A consent is given for one rhythm, and `billedOn` means "last collection" for monthly and yearly
 * but "done for good" for once: a mandate switched from monthly to once after a collection was
 * never collected again (ZVGPVQ). So the rhythm of an accepted mandate never changes, not even for
 * the administrative roles or a reset - a different rhythm is a new mandate. Only a Spende pledge
 * may move between monthly and yearly, where the marker means the same. A pledge is told by its
 * amount the payer picks, not by `canEditRhythm`: a school whose Elternengagement position names a
 * rhythm (Marienschule: monthly) stores its pledges with `canEditRhythm: false`.
 */
function assertRhythmUnchanged(stored: PartialEntityData<SepaMandat>, update: SepaMandat): void {
  if (!stored.accepted || !update.has('rhythm') || update.rhythm === stored.rhythm) return;
  if (!hasFixedAmount(stored) && PLEDGE_RHYTHMS.includes(stored.rhythm) && PLEDGE_RHYTHMS.includes(update.rhythm))
    return;

  throw new ExpectedError(
    'Der Rhythmus eines erteilten SEPA-Mandats kann nicht geändert werden. Für einen anderen Rhythmus das Mandat beenden und ein neues erteilen.',
  );
}

/**
 * The boundary for what only the administrative SEPA roles may do - the card hides it, but
 * `saveMandat` and the generic `updateEntity` take any write: changing or accepting a mandate while its group is
 * {@link assertMandateNotInBilling | in billing}, and moving its start against
 * {@link isStartMovableByOwner} or {@link isBackdatedStart | before today}. Server-internal writes
 * (jobs, resets, migrations) pass like the administrative roles. `stored` is null on a create.
 */
async function assertOwnerMayWrite(stored: Maybe<PartialEntityData<SepaMandat>>, update: SepaMandat): Promise<void> {
  if (await viewerCanOnPerson(SepaMandateActions.SEPA_MANDATE_MANAGE_OTHERS, null)) return;

  // the mandate as it is after the write - `updateEntity` may send only some fields
  const next = {
    accepted: update.has('accepted') ? update.accepted : stored?.accepted,
    rhythm: update.rhythm ?? stored?.rhythm,
    bookingID: update.bookingID ?? stored?.bookingID,
    billedOn: update.has('billedOn') ? update.billedOn : stored?.billedOn,
    start: update.has('start') ? update.start : stored?.start,
    end: update.has('end') ? update.end : stored?.end,
  };
  const accepting = isAcceptance(stored, next);
  await assertMandateNotInBilling(next, accepting);

  // a client save sends the whole mandate, so an unchanged start is no move
  if (stored && next.start?.getTime() === stored.start?.getTime()) return;
  // an acceptance picks a fresh start, only backdating it is refused
  if (stored && !accepting && !isStartMovableByOwner(stored))
    throw new ExpectedError(
      'Der Start eines SEPA-Mandats, das bereits begonnen hat oder abgerechnet wurde, kann nur von der Verwaltung geändert werden.',
    );
  if (isBackdatedStart(next.start, stored?.start))
    throw new ExpectedError('Ein SEPA-Mandat kann nicht rückwirkend beginnen.');
}
